import { Hourglass } from 'lucide-react'
import type { VenuePipelineDto } from '@lib/dto/pipeline.dto'
import { Card } from '@/components/ui/card'
import { PIPELINE_JUDGE_Z, PIPELINE_MIN_GAP } from '@lib/constants/pipeline'
import { formatAdDay, formatCount } from '@/lib/adFormat'
import { cn } from '@/lib/utils'

type Pace = NonNullable<VenuePipelineDto['pace']>

function bookings(n: number): string {
  return n === 1 ? 'booking' : 'bookings'
}

/**
 * Whether the span's bookings are behind or ahead of the usual by now, judged
 * as the steps are (docs/pipeline-view.spec.md, "What's usual"): only when
 * that's unlikely to be chance and at least two bookings apart.
 */
function paceVerdict(booked: number, usual: number): 'behind' | 'ahead' | null {
  const gap = booked - usual
  // Bookings come one at a time, so their chance spread is about the square root of how many are expected.
  if (usual <= 0 || Math.abs(gap) < PIPELINE_MIN_GAP || Math.abs(gap) / Math.sqrt(usual) < PIPELINE_JUDGE_Z) return null
  return gap < 0 ? 'behind' : 'ahead'
}

/** The span's bookings against the usual by now, as the page says it. */
function paceSentences(pace: Pace): string[] {
  const booked = pace.periods.reduce((a, p) => a + p.booked, 0)
  const usual = pace.periods.reduce((a, p) => a + p.usualByNow, 0)
  const verdict = paceVerdict(booked, usual)
  const gap = Math.max(1, Math.round(Math.abs(booked - usual)))
  const more = Math.round(pace.stillToCome)
  const weeks = Math.max(1, Math.ceil(pace.bookWithinDays / 7))
  return [
    `Most couples who book here do so within ${String(weeks)} ${weeks === 1 ? 'week' : 'weeks'} of inquiring.`,
    verdict === null
      ? 'These couples are booking about as usual for their age.'
      : `Against what’s usual at the same age, they’re about ${formatCount(gap)} ${bookings(gap)} ${verdict}.`,
    ...(more >= 1 ? [`Couples like these usually go on to make about ${formatCount(more)} more ${bookings(more)}.`] : []),
  ]
}

/** Above this many periods, only every few are named under the chart, so twelve months fit a phone. */
const NAMED_PERIODS = 8

/** Each period's bookings so far beside what's usual by now, both as shares of its inquiries so periods of any size compare. */
function PaceChart({ pace }: { pace: Pace }) {
  const rate = (n: number, of: number): number => (of === 0 ? 0 : n / of)
  const top = Math.max(0.01, ...pace.periods.map((p) => Math.max(rate(p.booked, p.inquiries), rate(p.usualByNow, p.inquiries))))
  const many = pace.periods.length > NAMED_PERIODS
  const every = Math.ceil(pace.periods.length / NAMED_PERIODS)
  const named = (i: number): boolean => !many || i % every === 0
  const described = pace.periods
    .map((p) => `${formatAdDay(p.start)}: ${String(p.booked)} of ${String(p.inquiries)} booked, usually ${String(Math.round(p.usualByNow))} by now`)
    .join('; ')
  return (
    <div role="img" aria-label={`Booked so far by inquiry period, against what’s usual at the same age. ${described}`} className="min-w-0 flex-[2_1_22rem] space-y-2">
      <div className={cn('flex h-28 items-end border-b border-line px-1', many ? 'gap-0.5' : 'gap-3')}>
        {pace.periods.map((p) => (
          <div key={p.start} className="relative h-24 min-w-0 flex-1">
            <div
              className="absolute inset-x-0 bottom-0 rounded-t-sm border-[1.5px] border-b-0 border-dashed border-line-strong"
              style={{ height: `${String((rate(p.usualByNow, p.inquiries) / top) * 100)}%` }}
            />
            <div
              className="absolute inset-x-[22%] bottom-0 rounded-t-xs bg-primary"
              style={{ height: `${String(Math.max(2, (rate(p.booked, p.inquiries) / top) * 100))}%` }}
            />
          </div>
        ))}
      </div>
      <div className={cn('flex px-1', many ? 'gap-0.5' : 'gap-3')}>
        {pace.periods.map((p, i) => (
          // Every few periods named when there are many; the rest keep their column, empty.
          <div key={p.start} className={cn('min-w-0 flex-1 whitespace-nowrap', many ? 'text-left' : 'text-center')}>
            {named(i) ? (
              <>
                <p className="m-0 text-xs font-medium text-ink">{formatAdDay(p.start)}</p>
                {many ? null : (
                  <p className="m-0 font-mono text-xs text-ink-muted tabular-nums">
                    {formatCount(p.booked)} of {formatCount(p.inquiries)}
                  </p>
                )}
              </>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * How the span's bookings are pacing (docs/pipeline-view.spec.md, "Still
 * deciding"): one card in place of a still-moving mark on every row, saying how
 * long couples here take to book, whether these are ahead or behind the usual
 * at the same age, and what is still to come.
 */
export function PacePanel({ pace }: { pace: Pace }) {
  const toCome = pace.stillToCome >= 1
  return (
    <Card asChild>
      <section aria-labelledby="pace-heading" className="flex flex-wrap items-stretch gap-x-10 gap-y-5 p-5">
        <div className="flex min-w-0 flex-[1_1_18rem] flex-col gap-3">
          <h2 id="pace-heading" className="m-0 flex items-center gap-2 text-heading font-semibold text-ink">
            <Hourglass aria-hidden className="h-4 w-4 text-ink-muted" />
            {toCome ? 'Still deciding' : 'How bookings are pacing'}
          </h2>
          {paceSentences(pace).map((line) => (
            <p key={line} className="m-0 text-sm text-ink">
              {line}
            </p>
          ))}
          <div className="mt-auto flex flex-wrap gap-4 text-xs text-ink-muted">
            <span className="inline-flex items-center gap-1.5">
              <span aria-hidden className="inline-block h-3 w-3 rounded-xs bg-primary" />
              Booked so far
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span aria-hidden className="inline-block h-3 w-3 rounded-xs border-[1.5px] border-dashed border-line-strong" />
              Usual at the same age
            </span>
          </div>
        </div>
        <PaceChart pace={pace} />
      </section>
    </Card>
  )
}
